(() => {
  const normalize = (value) =>
    String(value || '')
      .normalize('NFKD')
      .replace(/[\u0591-\u05C7]/g, '')
      .trim()
      .toLocaleLowerCase();

  const tokens = (value) => normalize(value).split(/\s+/).filter(Boolean);

  const setupDirectory = async (root) => {
    if (!root || root.dataset.directoryReady === 'true') return;
    root.dataset.directoryReady = 'true';
    if (window.SpiritCatalog && root.querySelector('[data-directory-category], [data-directory-treatment]')) {
      try { await window.SpiritCatalog.enrichDirectory(root); }
      catch (error) { console.warn('Spirit directory filters could not be completed', error); }
      if (!root.isConnected) return;
    }

    const mode = root.dataset.directoryMode || 'both';
    const form = root.querySelector('[data-directory-controls]');
    const grid = root.querySelector('[data-directory-grid]');
    if (!form || !grid) return;

    const cardSelector = mode === 'providers'
      ? '[data-directory-provider-card]'
      : '[data-directory-treatment-card]';
    const cards = Array.from(grid.querySelectorAll(cardSelector));
    const search = form.querySelector('[data-directory-search]');
    const category = form.querySelector('[data-directory-category]');
    const categoryCurrent = form.querySelector('[data-directory-category-current]');
    const categoryOptions = Array.from(form.querySelectorAll('[data-directory-category-option]'));
    const treatment = form.querySelector('[data-directory-treatment]');
    const role = form.querySelector('[data-directory-role]');
    const location = form.querySelector('[data-directory-location]');
    const sort = form.querySelector('[data-directory-sort]');
    const count = root.querySelector('[data-directory-count]');
    const empty = root.querySelector('[data-directory-empty]');
    const nextPage = root.querySelector('[data-directory-next]');
    const loadedPages = new Set([root.dataset.directoryPage || '1']);
    if (Number(root.dataset.directoryPage) > 1) nextPage?.setAttribute('href', '?page=1');
    let loadingPages = false;
    const filterControls = Array.from(form.querySelectorAll('[data-directory-filter-control]'));
    const syncFilterControls = () => {
      filterControls.forEach((control) => {
        const select = control.querySelector('select');
        const trigger = control.querySelector('[data-directory-filter-trigger]');
        const current = control.querySelector('[data-directory-filter-current]');
        if (!select || !trigger || !current) return;
        const selectedLabel = select.selectedOptions[0]?.textContent || '';
        const label = select.getAttribute('aria-label');
        current.textContent = selectedLabel;
        trigger.setAttribute('aria-label', selectedLabel === label ? label : `${label}: ${selectedLabel}`);
        control.querySelectorAll('[data-directory-filter-options] button').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.value === select.value));
        });
      });
    };
    if (filterControls.length) {
      const mobileFilters = window.matchMedia('(max-width: 749px)');
      const updateFilterViewport = () => {
        if (!root.isConnected) {
          mobileFilters.removeEventListener('change', updateFilterViewport);
          return;
        }
        filterControls.forEach((control) => {
          const menu = control.querySelector('[data-directory-filter-menu]');
          control.querySelector('[data-directory-filter-native]').hidden = mobileFilters.matches;
          menu.hidden = !mobileFilters.matches;
          if (!mobileFilters.matches) menu.open = false;
        });
      };
      filterControls.forEach((control) => {
        const select = control.querySelector('select');
        const menu = control.querySelector('[data-directory-filter-menu]');
        const trigger = menu.querySelector('summary');
        const options = menu.querySelector('[data-directory-filter-options]');
        Array.from(select.options).forEach((option) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = option.textContent;
          button.dataset.value = option.value;
          button.disabled = option.disabled;
          button.addEventListener('click', () => {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            menu.open = false;
            trigger.focus({ preventScroll: true });
          });
          options.append(button);
        });
        menu.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          menu.open = false;
          trigger.focus({ preventScroll: true });
        });
        document.addEventListener('click', (event) => {
          if (menu.open && !menu.contains(event.target)) menu.open = false;
        });
        menu.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !menu.contains(event.relatedTarget)) menu.open = false;
        });
      });
      mobileFilters.addEventListener('change', updateFilterViewport);
      updateFilterViewport();
    }

    const syncCategoryMenu = () => {
      if (!category || categoryOptions.length === 0) return;
      const currentValue = category.value || '';
      const selectedOption = categoryOptions.find((option) => (option.dataset.value || '') === currentValue);
      if (selectedOption && categoryCurrent) {
        categoryCurrent.textContent = selectedOption.dataset.label || selectedOption.textContent.trim();
      }
      categoryOptions.forEach((option) => {
        const isSelected = (option.dataset.value || '') === currentValue;
        option.classList.toggle('is-active', isSelected);
        option.setAttribute('aria-pressed', String(isSelected));
      });
    };

    const updateCount = (visibleCount) => {
      if (!count) return;
      const template = count.dataset.countTemplate || '[count]';
      count.textContent = template.replaceAll('[count]', String(visibleCount));
    };

    const apply = () => {
      syncFilterControls();
      const searchValue = normalize(search?.value);
      const categoryValue = normalize(category?.value);
      const treatmentValue = normalize(treatment?.value);
      const roleValue = normalize(role?.value);
      const locationValue = normalize(location?.value);

      cards.forEach((card) => {
        const titleMatch = !searchValue || normalize(card.dataset.title).includes(searchValue);
        const categoryMatch = !categoryValue || tokens(card.dataset.categories).includes(categoryValue);
        const treatmentMatch = !treatmentValue || tokens(card.dataset.treatments).includes(treatmentValue);
        const cardRole = normalize(card.dataset.role);
        const roleMatch = !roleValue || cardRole === roleValue ||
          (cardRole === 'facilitator' && roleValue === 'instructor') ||
          (cardRole === 'both' && ['therapist', 'instructor'].includes(roleValue));
        const locationMatch = !locationValue || normalize(card.dataset.location) === locationValue;
        card.hidden = !(titleMatch && categoryMatch && treatmentMatch && roleMatch && locationMatch);
      });

      const sortValue = sort?.value || 'default';
      const sortedCards = [...cards].sort((first, second) => {
        if (sortValue === 'name') {
          return normalize(first.dataset.name || first.dataset.title).localeCompare(normalize(second.dataset.name || second.dataset.title), 'he');
        }
        if (sortValue === 'providers') {
          return Number(second.dataset.providers || 0) - Number(first.dataset.providers || 0);
        }
        const firstOrder = Number(first.dataset.order || first.dataset.index || 0);
        const secondOrder = Number(second.dataset.order || second.dataset.index || 0);
        return firstOrder - secondOrder;
      });
      sortedCards.forEach((card) => grid.append(card));

      const visibleCount = cards.filter((card) => !card.hidden).length;
      updateCount(visibleCount);
      if (empty) empty.hidden = visibleCount !== 0 || Boolean(nextPage?.getAttribute('href'));
    };

    if (mode !== 'providers') {
      const url = new URL(window.location.href);
      if (url.searchParams.has('emotion')) {
        url.searchParams.delete('emotion');
        window.history.replaceState({}, '', url);
      }
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      apply();
    });
    form.addEventListener('input', apply);
    form.addEventListener('change', apply);
    form.addEventListener('reset', () => {
      requestAnimationFrame(() => {
        syncCategoryMenu();
        apply();
      });
    });

    categoryOptions.forEach((option) => {
      option.addEventListener('click', () => {
        if (!category) return;
        category.value = option.dataset.value || '';
        syncCategoryMenu();
        category.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    syncCategoryMenu();
    apply();

    const loadRemainingPages = async () => {
      if (!nextPage || loadingPages || !nextPage.getAttribute('href')) return;
      loadingPages = true;
      grid.setAttribute('aria-busy', 'true');
      nextPage.hidden = true;
      const visited = new Set();
      try {
        while (nextPage.getAttribute('href') && root.isConnected) {
          const url = new URL(nextPage.getAttribute('href'), window.location.href);
          new URL(window.location.href).searchParams.forEach((value, key) => {
            if (key !== 'page' && key !== 'section_id' && !url.searchParams.has(key)) url.searchParams.set(key, value);
          });
          url.searchParams.set('section_id', root.dataset.directorySection);
          if (url.origin !== window.location.origin || visited.has(url.href)) throw new Error('Invalid directory page');
          visited.add(url.href);
          const response = await fetch(url, { headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Directory page unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = parsed.querySelector('[data-spirit-directory]');
          if (!source || source.dataset.directorySection !== root.dataset.directorySection) throw new Error('Invalid directory response');
          if (!root.isConnected) return;
          const identity = card => card.dataset.providerId || card.dataset.treatmentId;
          const known = new Map(cards.map(card => [identity(card), card]));
          source.querySelectorAll(`[data-directory-grid] ${cardSelector}`).forEach(card => {
            if (loadedPages.has(source.dataset.directoryPage)) return;
            const id = identity(card);
            if (!id) return;
            const existing = known.get(id);
            if (existing) {
              if (card.dataset.treatmentId) {
                existing.dataset.providers = String(Number(existing.dataset.providers || 0) + Number(card.dataset.providers || 0));
                existing.querySelectorAll('[data-treatment-count]').forEach(count => { count.textContent = existing.dataset.providers; });
                let avatars = existing.querySelector('[data-treatment-avatars]');
                const additionalAvatars = card.querySelector('[data-treatment-avatars]');
                if (!avatars && additionalAvatars) {
                  avatars = additionalAvatars.cloneNode(false);
                  existing.querySelector('.spirit-directory-treatment-card__content').append(avatars);
                }
                if (avatars) {
                  avatars.removeAttribute('aria-label');
                  [...(additionalAvatars?.children || [])].slice(0, Math.max(0, 5 - avatars.children.length)).forEach(avatar => avatars.append(avatar));
                }
              }
              return;
            }
            known.set(id, card);
            card.dataset.index = String(cards.length);
            grid.append(card);
            cards.push(card);
          });
          if (treatment) {
            source.querySelectorAll('[data-directory-treatment] option').forEach(option => {
              if (![...treatment.options].some(current => current.value === option.value)) treatment.append(option);
            });
          }
          if (category) {
            source.querySelectorAll('[data-directory-category] option').forEach(option => {
              if ([...category.options].some(current => current.value === option.value)) return;
              category.append(option);
              const sourceButton = [...source.querySelectorAll('[data-directory-category-option]')].find(button => button.dataset.value === option.value);
              if (!sourceButton) return;
              const button = sourceButton.cloneNode(true);
              button.addEventListener('click', () => {
                category.value = button.dataset.value || '';
                syncCategoryMenu();
                category.dispatchEvent(new Event('change', { bubbles: true }));
              });
              form.querySelector('.spirit-directory__category-options')?.append(button);
              categoryOptions.push(button);
            });
          }
          if (location) {
            source.querySelectorAll('[data-directory-location] option').forEach(option => {
              if ([...location.options].some(current => current.value === option.value)) return;
              location.append(option.cloneNode(true));
              const control = location.closest('[data-directory-filter-control]');
              const options = control?.querySelector('[data-directory-filter-options]');
              if (!options) return;
              const button = document.createElement('button');
              button.type = 'button';
              button.textContent = option.textContent;
              button.dataset.value = option.value;
              button.addEventListener('click', () => {
                location.value = option.value;
                location.dispatchEvent(new Event('change', { bubbles: true }));
                control.querySelector('details').open = false;
                control.querySelector('summary').focus({ preventScroll: true });
              });
              options.append(button);
            });
          }
          loadedPages.add(source.dataset.directoryPage);
          const next = source.dataset.directoryNextUrl;
          if (next) nextPage.setAttribute('href', next);
          else nextPage.removeAttribute('href');
          apply();
        }
      } catch {
        nextPage.hidden = false;
      } finally {
        loadingPages = false;
        grid.removeAttribute('aria-busy');
        apply();
      }
    };
    nextPage?.addEventListener('click', event => {
      event.preventDefault();
      loadRemainingPages();
    });
    loadRemainingPages();

    const rail = root.querySelector('[data-directory-provider-rail]');
    if (rail) {
      root.querySelectorAll('[data-directory-rail-button]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.directoryRailButton === 'next' ? -1 : 1;
          const distance = Math.max(rail.clientWidth * 0.8, 240);
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          rail.scrollBy({ left: direction * distance, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });
    }
  };

  const setupAll = (scope = document) => {
    scope.querySelectorAll('[data-spirit-directory]').forEach(setupDirectory);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupAll());
  } else {
    setupAll();
  }

  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();
